/**
 * Inline QC rules template - Activision CODM Paid UA v1.1.
 *
 * Mirrors `backend/tests/fixtures/rules/examples/codm_ua_v1_1.md` so QA
 * can paste a working ruleset into the Submit form with one click and then
 * tweak severities / cues / aliases for debugging without re-uploading.
 *
 * Keep this file in sync with the backend fixture when rules change.
 * Phase 12C: includes `anti_cues` blocks for blood, gamer_tags, headshot,
 * wallhack_visibility, and brand_logo to suppress look-alike false positives.
 * Phase 13C: gamer_tags / headshot / wallhack_visibility now declare
 * `applicable_scenes` so the scene classifier can structurally drop
 * findings raised on menu-UI / end-card frames. Brittle end-card anti_cues
 * removed in favour of that gate.
 */
export const CODM_V1_1_TEMPLATE = `---
schema_version: "1"
default_match_threshold: 0.72
source_format: frontmatter
metadata:
  version: "1.1"
  scope: "CODM Paid UA, Social, Store Assets, UGC-Style Creatives"
  authority: "Activision Brand & Legal Standards"
rules:
  # ----- §3 CRITICAL VIOLATIONS (AUTO-FAIL) -----
  - id: blood
    label: Blood depiction (§3.1)
    severity: critical
    verdict_action: fail
    mandatory: true
    aliases:
      - blood splatter
      - blood spray
      - blood decal
      - blood pooling
      - gore
      - red liquid from gunshot
      - slow-motion kill impact
    visual_cues:
      - "red liquid spray emerging from a character on bullet impact"
      - "red pooling, decals, or stains on the ground, walls, or character bodies"
      - "red mist or particle burst around a character during a kill"
      - "a red splatter / streak overlay that is character-anchored (i.e. attached to a specific human silhouette being shot) — NOT the global edge-of-screen damage vignette"
    anti_cues:
      - "the standard FPS damage-taken vignette: a translucent red glow along the screen edges that appears uniformly around the entire frame when the player is being hit — this is a HUD effect, not blood"
      - "red brick walls, red brand colours, red product packaging, red signage or red lighting present in the environment"
      - "enemy or teammate health bars, hit-marker indicators, damage-number popups, or kill-feed banners drawn in red"
      - "red laser sight beams, red reticles, red crosshairs, or red aim-assist dots"
      - "red muzzle flash from a weapon firing"
  - id: nuclear_imagery
    label: Nuclear imagery (§3.2)
    severity: critical
    verdict_action: fail
    mandatory: true
    aliases:
      - nuclear explosion
      - mushroom cloud
      - radiation symbol
      - nuke killstreak
      - city destruction from nuclear blast
  - id: crash_site
    label: Aircraft crash site imagery (§3.3)
    severity: critical
    verdict_action: fail
    mandatory: true
    aliases:
      - helicopter crash
      - plane crash
      - burning aircraft wreckage
      - aircraft debris field
      - smoking aircraft remains
  - id: religious_symbol
    label: Religious symbols (§3.4)
    severity: critical
    verdict_action: fail
    mandatory: true
    aliases:
      - christian cross
      - cross necklace
      - church iconography
  - id: gamer_tags
    label: Unblurred gamer tags / player usernames (§3.5)
    severity: critical
    verdict_action: fail
    mandatory: true
    applicable_scenes:
      - gameplay
      - kill_cam
      - scoreboard
    aliases:
      - visible username
      - readable scoreboard id
      - readable kill feed name
      - player chat username
      - gamer tags not blurred
    visual_cues:
      - "a short text string (typically 3-16 characters: letters, digits, or underscores) rendered as a *floating name tag directly above a specific player character's head* in 3D space, following the character as they move"
      - "a username column inside a kill-feed banner, scoreboard row, or end-of-match leaderboard — i.e. text inside a structured player-roster UI — that is NOT redacted/blurred/masked"
      - "a player chat handle visible in an in-game chat bubble or speech indicator attached to a character"
      - "a clan tag in brackets followed by a screen-name pattern (e.g. '[XYZ] PlayerName') attached to a character, scoreboard, or kill feed"
    anti_cues:
      - "subtitle, caption, or burned-in dialogue translation overlays at the top or bottom of the screen"
      - "kill counter, score widget, round-timer numbers, ammo count, killstreak indicator, or other purely numeric HUD widgets (these are stats, not usernames)"
      - "the 'CALL OF DUTY MOBILE' brand wordmark, mode name, map name, season label, or other in-game UI label text"
      - "on-screen button prompts or control hints (e.g. 'PRESS X TO RELOAD', 'TAP TO FIRE', 'HOLD TO AIM')"
      - "weapon names, attachment names, loadout labels, operator names, or perk names displayed in the HUD or weapon-select screens"
      - "objective markers, waypoint labels, or capture-zone callouts (e.g. 'ALPHA', 'BRAVO', 'A SITE') floating in the world"
      - "damage numbers, hit-marker text, XP popups, or medal callouts that flash briefly and are not attached to a specific persistent character"
  - id: real_weapon_footage
    label: Real-world weapon footage (§3.6)
    severity: critical
    verdict_action: fail
    mandatory: true
    aliases:
      - live action firearm
      - real gun handling
      - non-gameplay weapon footage
  - id: headshot
    label: Headshot depiction or emphasis (§3.7)
    severity: critical
    verdict_action: fail
    mandatory: true
    applicable_scenes:
      - gameplay
      - kill_cam
      - scoreboard
    aliases:
      - on-screen headshot text
      - headshot medal
      - headshot kill notification
      - bullet impact to the head
      - slow-motion headshot impact
    visual_cues:
      - "an on-screen text overlay whose literal content is the word 'HEADSHOT' (or a localised equivalent such as 'TIR À LA TÊTE', 'ヘッドショット', '爆头') rendered as a momentary kill-feedback banner near a character that was just killed"
      - "a medal / award icon depicting a stylised crosshair-on-silhouette or bullet-through-head graphic awarded after a kill, accompanied by an explicit 'HEADSHOT' label"
      - "a sniper-scope badge whose label explicitly counts head kills (e.g. 'HEADSHOTS: 7') shown inside a scoring summary or end-of-match leaderboard"
      - "a slow-motion or stylised camera move that *frames the bullet impact landing on a character's head* (impact point and head must both be unambiguously in frame)"
      - "an end-card statistic line that uses the word 'HEADSHOT(S)' as a counted-stat label (not generic kill stats)"
    anti_cues:
      - "a generic 'KILL', 'ELIMINATED', 'DOUBLE KILL', 'TRIPLE KILL', 'NICE SHOT', or 'GOOD GAME' notification with no explicit head-specific wording or iconography"
      - "a generic skull icon appearing in a pause menu, death screen, operator selection, attachment menu, or as a cosmetic weapon decal / skin (cosmetic art, not a headshot medal)"
      - "a weapon name accompanied by a skull-themed skin, charm, or sticker (the skull is decoration, not a kill medal)"
      - "the live aiming reticle / crosshair shown on screen during normal aiming (the headshot badge is a *post-kill medal*, structurally distinct from the live aim reticle)"
      - "any bullet impact, gunfire, or kill-cam where the camera is on the torso, weapon, or full body but the head is NOT the unambiguous impact point"

  # ----- §4 MAJOR VIOLATIONS (REVIEW REQUIRED) -----
  - id: viewer_targeted_weapon
    label: First-person weapon aimed at viewer (§4.1)
    severity: major
    verdict_action: review
    mandatory: true
    aliases:
      - weapon aimed at camera
      - threatening framing
      - slow push-in aiming shot
  - id: excessive_violence
    label: Excessive violence (§4.2)
    severity: major
    verdict_action: review
    mandatory: true
    aliases:
      - prolonged kill shot
      - civilian harm
      - torture framing
      - execution framing
      - realistic injury emphasis
  - id: misleading_gameplay
    label: Misleading or fabricated gameplay (§4.3)
    severity: major
    verdict_action: review
    mandatory: true
    aliases:
      - fake ui
      - impossible mechanics
      - fabricated outcome
      - guaranteed wins
      - "#1 claim"
  - id: third_party_ip
    label: Third-party IP visible (§4.4)
    severity: major
    verdict_action: review
    mandatory: true
    aliases:
      - visible logo
      - branded apparel
      - posters or artwork
      - landmark imagery
      - unlicensed music
  - id: wallhack_visibility
    label: Wallhack-style gameplay visibility (§4.5)
    severity: major
    verdict_action: review
    mandatory: true
    applicable_scenes:
      - gameplay
      - kill_cam
    aliases:
      - invisible ghost people
      - ghosted soldiers through walls
      - silhouettes through walls
      - teammates through walls
      - enemy outlines through walls
      - perspective teammate
    visual_cues:
      - "saturated blue or cyan glowing outline traced around a human-shaped character"
      - "enemy or teammate silhouette visibly rendered THROUGH opaque cover (truck, wall, building, container)"
      - "highlighted character silhouette visible through dust, smoke, or foliage that would normally hide them"
      - "coloured perimeter halo around a soldier where the rest of the body is occluded"
    anti_cues:
      - "objective markers, waypoint icons, or capture-zone indicators floating in the world (these are UI, not character outlines)"
      - "minimap dots representing teammates or enemies (top-down icons, not in-world silhouettes)"
      - "a teammate or enemy clearly visible in plain line of sight with no occluding cover — i.e. not through a wall"
      - "friendly nameplates or hit-marker chevrons floating above teammates in open space"
      - "smoke grenade or muzzle flash effects (volumetric particles, not character silhouettes)"
  - id: brand_logo
    label: Brand logo placement / safe-area issue (§4.6)
    severity: major
    verdict_action: review
    mandatory: true
    aliases:
      - logo cropped
      - logo cut off
      - logo top left cut off
      - logo obscured
      - logo distorted
      - logo too close to edge
    visual_cues:
      - "the 'CALL OF DUTY MOBILE' wordmark in the top-left corner with any letter clipped, cut off, or extending beyond the visible frame edge"
      - "the COD logo touching or crossing the safe-area boundary (no padding from the frame edge)"
      - "the logo overlapping HUD elements or partially obscured by gameplay UI"
      - "a stretched, squashed, recoloured, or otherwise distorted version of the COD logo"
    anti_cues:
      - "in-world game signage, billboards, or environmental text that is NOT the 'CALL OF DUTY MOBILE' brand wordmark"
      - "loadout, weapon, or operator name labels displayed in the HUD"
      - "third-party logos on apparel or vehicles (those are §4.4 third-party IP, not the brand mark itself)"
      - "the wordmark fully visible inside the safe area with comfortable padding from every edge"
  - id: subtitle_styling
    label: Subtitle styling error (§4.7)
    severity: major
    verdict_action: review
    mandatory: true
    aliases:
      - subtitles in wrong font
      - subtitles in wrong colour
      - inconsistent typeface
      - illegible subtitle
      - subtitle over action
  - id: cta_missing
    label: CTA / store badge missing or illegible (§4.8)
    severity: major
    verdict_action: review
    mandatory: true
    aliases:
      - app store button missing
      - google play badge missing
      - store badge cropped
      - cta hidden
      - end card missing
  - id: copy_placement
    label: Copy poorly placed over gameplay (§4.9)
    severity: major
    verdict_action: review
    mandatory: true
    aliases:
      - copy poorly placed over gameplay
      - copy covering key action
      - copy overlapping subtitles
      - copy outside safe area
      - copy with insufficient contrast

  # ----- §6.1 AUDIO CONTINUITY (REVIEW) -----
  - id: audio_continuity
    label: Music / SFX continuity break (§6.1)
    severity: major
    verdict_action: review
    mandatory: true
    aliases:
      - music cuts out
      - sfx cuts out
      - audio drop
      - sudden silence
      - missing end-card sting
      - abrupt audio transition
---

# CODM_UA_QC_AGENT.md

## Call of Duty®: Mobile — Automated QC Agent Instructions

Version: 1.1
Scope: Paid UA, Social, Store Assets, UGC-Style Creatives
Authority: Activision Brand & Legal Standards

---

# 1. Agent Purpose

You are a compliance QC agent responsible for reviewing marketing creatives for **Call of Duty®: Mobile**.

Your job is to:

- Detect brand violations
- Detect legal risks
- Detect ad platform policy risks
- Flag sensitive or prohibited content
- Detect gameplay, UI, audio, subtitle, and CTA execution issues
- Classify issues by severity
- Recommend \`PASS\`, \`REVIEW\`, or \`FAIL\`

You must be conservative. When uncertain, escalate.

---

# 2. Review Scope

You must scan:

- All video frames
- Static frames
- UI overlays
- Audio transcript
- On-screen text
- Metadata
- Visible usernames
- Background details
- Logo placement and safe areas
- Subtitle styling, placement, colour, and font
- CTA / end-card presence and placement
- Audio continuity, including music and SFX throughout the full video
- Gameplay UI settings that may expose prohibited or misleading information

---

# 3. Critical Violations (AUTO-FAIL)

If any of the following are detected, immediately return Status: \`FAIL\`, Severity: \`CRITICAL\`. Do not downgrade.

## 3.1 Blood Prohibition
Fail on blood splatter, decals, pooling, spray, red liquid from gunshot, or slow-motion gore.

## 3.2 Nuclear Imagery
Fail on nuclear explosion, mushroom cloud, radiation symbol, nuke killstreak, or city destruction from nuclear blast.

## 3.3 Crash Site Imagery
Fail on helicopter or plane crash sites, burning aircraft wreckage, debris fields, or smoking aircraft remains.

## 3.4 Religious Symbols
Fail on christian cross, cross necklace, church iconography, or any identifiable religious symbol.

## 3.5 Gamer Tags
Fail if any gamer tag, player username, scoreboard ID, kill feed name, or chat username is readable. All must be blurred.

## 3.6 Real-World Weapon Footage
Fail if live-action firearms, real-world gun handling, or non-gameplay weapon footage appears.

## 3.7 Headshot Depiction
Fail if the creative depicts, celebrates, or emphasizes a headshot in text, medal, kill notification, slow-motion impact, or voiceover.

---

# 4. Major Violations (REVIEW REQUIRED)

Return Status: \`REVIEW\`, Severity: \`MAJOR\`.

- §4.1 Viewer-Targeted Weapon Framing
- §4.2 Excessive Violence
- §4.3 Misleading Gameplay
- §4.4 Third-Party IP
- §4.5 Wallhack-Style Visibility (incl. Perspective Teammate through walls)
- §4.6 Brand Logo Placement / Safe Area (Call of Duty logo top-left cut off)
- §4.7 Subtitle Styling (wrong font, wrong colour, inconsistent typeface)
- §4.8 CTA / Store Badge missing or illegible (App Store / Google Play)
- §4.9 Copy Placement Over Gameplay

---

# 5. Minor Flags (PASS WITH NOTES)

Aggressive but compliant tone, fast-cut intensity near threshold, borderline dramatic framing, CTA urgency language, minor subtitle timing slips.

---

# 6. Audio Analysis Rules

Fail on copyrighted music, recognizable melody, religious references, political references.
Review on aggressive tone, fear-based messaging, broken or unintentionally silent audio mix.

## 6.1 Music & SFX Continuity
Flag for \`REVIEW\` (MAJOR) if music, SFX, or key game audio cuts out unexpectedly mid-video.

---

# 7. Text Analysis Rules

Fail on political/religious content, personal data, hate symbols, profanity, readable gamer tags, headshot copy.
Review on misleading performance claims, exaggerated guarantees, subtitle font/colour issues, copy placement, missing CTA/logo issues.

---

# 8. Specific CODM UA Watchout Checklist

The agent must emit a per-row pass/fail note for each watchout below, even when no issue is found:

| Watchout | Action | Severity |
|---|---:|---:|
| Call of Duty logo top left is cut off | brand/logo safe-area review | MAJOR |
| Blood splatter | Fail immediately | CRITICAL |
| Invisible ghost people shown | gameplay visibility review | MAJOR |
| Perspective Teammate switched on (teammates through walls) | gameplay visibility review | MAJOR |
| Subtitles in wrong font | subtitle styling review | MAJOR |
| Subtitles in wrong colour | subtitle styling review | MAJOR |
| Music and SFX cuts out half way through video | audio continuity review | MAJOR |
| Headshot | Fail if depicted/emphasized | CRITICAL |
| App Store button missing | CTA/store badge review | MAJOR |
| Gamer tags not blurred | Fail immediately | CRITICAL |
| Copy poorly placed over gameplay | layout/readability review | MAJOR |

---

# 9. Output Format

Final Status: \`PASS\` / \`REVIEW\` / \`FAIL\`
Severity Level: \`NONE\` / \`MINOR\` / \`MAJOR\` / \`CRITICAL\`

Detected Issues — for each: type, location (timestamp or frame), description, rule violated, severity.

Specific CODM UA Watchout Checklist — one row per watchout from §8.

Risk Summary — Brand / Legal / Platform = LOW / MEDIUM / HIGH.

Recommendation — Approve / Blur or Edit / Remove Scene / Escalate to Legal / Reject Asset.

---

# 10. Escalation Protocol

Escalate on multiple major violations, unclear religious symbol, uncertain crash imagery, possible blood/nuclear/headshot/gamer-tag/wallhack issues, unclear CTA/logo issues, unintentional audio cut. When uncertain → default to \`REVIEW\`.

---

# 11. Golden Rule

If the creative risks platform rejection, legal exposure, brand damage, public backlash, misleading gameplay, or poor install conversion → do not approve. Conservative over permissive.
`;
